import { nullableOptional } from '@shigo-ui/api-client';
import { z } from 'zod';

export enum ChangelogCategory {
  Placement = 'PLACEMENT',
  Publisher = 'PUBLISHER',
  Integration = 'INTEGRATION',
  LineItem = 'LINE_ITEM',
  PublisherDomain = 'PUBLISHER_DOMAIN',
  PublisherApp = 'PUBLISHER_APP',
  App = 'APP',
  Targeting = 'TARGETING',
  AdsTxt = 'ADSTXT',
  DemandPartner = 'DEMAND_PARTNER',
  Deal = 'DEAL',
  Advertiser = 'ADVERTISER',
  Order = 'ORDER',
  Creative = 'CREATIVE',
  Video = 'VIDEO',
  ContentOwner = 'CONTENT_OWNER',
  Widget = 'WIDGET',
  User = 'USER',
  Asset = 'ASSET',
  Brand = 'BRAND',
  A2vBrand = 'A2V_BRAND',
  Playlist = 'PLAYLIST',
  A2vConfiguration = 'A2V_CONFIGURATION',
}

export enum ChangelogType {
  Create = 'CREATE',
  Update = 'UPDATE',
  Delete = 'DELETE',
}

export enum ChangelogSortProperty {
  CreatedOn = 'createdOn',
}

export enum ChangelogSortDirection {
  Asc = 'ASC',
  Desc = 'DESC',
}

export enum ChangelogLookupStatus {
  Active = 'ACTIVE',
}

const changelogContextValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

export const changelogSchema = z.looseObject({
  id: z.union([z.string(), z.number()]),
  category: z.enum(ChangelogCategory),
  type: z.enum(ChangelogType),
  createdOn: z.string(),
  fieldName: z.string(),
  oldValue: nullableOptional(z.string()),
  newValue: nullableOptional(z.string()),
  createdBy: nullableOptional(z.string()),
  source: nullableOptional(z.string()),
  context: z.record(z.string(), changelogContextValueSchema),
});

export const changelogPageSchema = z.looseObject({
  content: z.array(changelogSchema),
  totalElements: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative().optional(),
  number: z.number().int().nonnegative().optional(),
  size: z.number().int().positive().optional(),
});

export const changelogLookupOptionSchema = z.looseObject({
  id: z.coerce.string(),
  name: z.string(),
});

export const changelogUserLookupOptionSchema = z.looseObject({
  email: z.string(),
  name: z.string(),
});

export const changelogDemandPartnerLookupOptionSchema = z.looseObject({
  id: z.coerce.string(),
  displayName: z.string(),
});

export const changelogLookupPageSchema = z.looseObject({
  content: z.array(changelogLookupOptionSchema),
  totalElements: z.number().int().nonnegative().optional(),
});

export type ChangelogEntry = z.infer<typeof changelogSchema>;
export type ChangelogPage = z.infer<typeof changelogPageSchema>;
export type ChangelogLookupOption = z.infer<typeof changelogLookupOptionSchema>;
export type ChangelogUserLookupOption = z.infer<typeof changelogUserLookupOptionSchema>;
export type ChangelogDemandPartnerLookupOption = z.infer<
  typeof changelogDemandPartnerLookupOptionSchema
>;

export interface ChangelogFilter {
  publisherIds: string[];
  placementIds: string[];
  integrationIds: string[];
  demandPartnerIds: string[];
  createdBy: string;
  lineItemIds: string[];
  dateFrom: string;
  dateTo: string;
  page: number;
  size: number;
  sortProperty: ChangelogSortProperty | null;
  sortDirection: ChangelogSortDirection;
}

export const changelogPageSizes = [5, 10, 25, 50, 100] as const;

export function createDefaultChangelogFilter(referenceDate = new Date()): ChangelogFilter {
  const dateTo = startOfLocalDay(referenceDate);
  dateTo.setDate(dateTo.getDate() - 1);
  const dateFrom = new Date(dateTo);
  dateFrom.setDate(dateFrom.getDate() - 6);

  return {
    publisherIds: [],
    placementIds: [],
    integrationIds: [],
    demandPartnerIds: [],
    createdBy: '',
    lineItemIds: [],
    dateFrom: formatLocalDate(dateFrom),
    dateTo: formatLocalDate(dateTo),
    page: 0,
    size: 25,
    sortProperty: ChangelogSortProperty.CreatedOn,
    sortDirection: ChangelogSortDirection.Desc,
  };
}

export function isChangelogSortProperty(value: string): value is ChangelogSortProperty {
  return Object.values(ChangelogSortProperty).includes(value as ChangelogSortProperty);
}

function startOfLocalDay(value: Date) {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function formatLocalDate(value: Date) {
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${String(value.getFullYear())}-${month}-${day}`;
}
